import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthConfig, Principal } from './types';
import { getCurrentPrincipal, getAuthConfig } from '../lib/api/platformApi';
import {
  createHostedLoginRequest,
  exchangeAuthorizationCode,
  hasOauthCallback,
  refreshHostedSession,
  tokenExpired,
  type CognitoTokens,
} from '../features/auth/cognito';

interface DebugSessionInput {
  readonly tenantId: string;
  readonly role: Principal['role'];
  readonly email?: string;
  readonly subject?: string;
}

interface StoredSession {
  readonly mode: 'debug' | 'oauth';
  readonly principal: Principal;
  readonly debugHeaders?: Record<string, string>;
  readonly tokens?: CognitoTokens;
}

interface SessionContextValue {
  readonly principal: Principal | null;
  readonly authConfig: AuthConfig | null;
  readonly authBootstrapped: boolean;
  readonly authError: string;
  readonly signInWithDebug: (input: DebugSessionInput) => Promise<void>;
  readonly signInWithCognito: () => Promise<void>;
  readonly signOut: () => void;
  readonly authHeaders: Record<string, string>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);
const STORAGE_KEY = 'crime-platform-session';
const PKCE_VERIFIER_KEY = 'crime-platform-pkce-verifier';
const PKCE_STATE_KEY = 'crime-platform-pkce-state';

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [authHeaders, setAuthHeaders] = useState<Record<string, string>>({});
  const [authBootstrapped, setAuthBootstrapped] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (process.env.NODE_ENV === 'test') {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
        return;
      }

      try {
        const nextAuthConfig = await getAuthConfig();
        if (cancelled) {
          return;
        }
        setAuthConfig(nextAuthConfig);

        if (hasOauthCallback(new URL(window.location.href))) {
          await restoreOauthCallback(nextAuthConfig, setSessionState);
          return;
        }

        const stored = loadStoredSession();
        if (!stored) {
          setAuthBootstrapped(true);
          return;
        }

        if (stored.mode === 'debug') {
          if (!nextAuthConfig.debugAuthEnabled) {
            clearStoredSession();
            setAuthBootstrapped(true);
            return;
          }

          const livePrincipal = await getCurrentPrincipal(stored.debugHeaders ?? {});
          setSessionState(livePrincipal, stored.debugHeaders ?? {}, stored);
          return;
        }

        if (!stored.tokens) {
          clearStoredSession();
          setAuthBootstrapped(true);
          return;
        }

        const tokens =
          tokenExpired(stored.tokens) && stored.tokens.refreshToken
            ? await refreshHostedSession(nextAuthConfig, stored.tokens.refreshToken)
            : stored.tokens;

        if (tokenExpired(tokens) && !tokens.refreshToken) {
          clearStoredSession();
          setAuthBootstrapped(true);
          return;
        }

        const oauthHeaders = authorizationHeaders(tokens);
        const livePrincipal = await getCurrentPrincipal(oauthHeaders);
        setSessionState(livePrincipal, oauthHeaders, {
          mode: 'oauth',
          principal: livePrincipal,
          tokens,
        });
      } catch (error) {
        clearStoredSession();
        clearPkceSession();
        if (!cancelled) {
          setAuthError(error instanceof Error ? error.message : 'Failed to initialize authentication.');
          setAuthBootstrapped(true);
        }
      }
    };

    const setSessionState = (
      nextPrincipal: Principal,
      nextHeaders: Record<string, string>,
      storedSession: StoredSession
    ) => {
      if (cancelled) {
        return;
      }

      setPrincipal(nextPrincipal);
      setAuthHeaders(nextHeaders);
      persistSession(storedSession);
      setAuthError('');
      setAuthBootstrapped(true);
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      principal,
      authConfig,
      authBootstrapped,
      authError,
      authHeaders,
      async signInWithDebug(input: DebugSessionInput) {
        if (!authConfig?.debugAuthEnabled) {
          throw new Error('Debug sign-in is not enabled for this stage.');
        }

        const debugHeaders: Record<string, string> = {
          'x-debug-tenant-id': input.tenantId,
          'x-debug-role': input.role,
        };

        if (input.email) {
          debugHeaders['x-debug-email'] = input.email;
        }
        if (input.subject) {
          debugHeaders['x-debug-subject'] = input.subject;
        }

        const livePrincipal = await getCurrentPrincipal(debugHeaders);
        setPrincipal(livePrincipal);
        setAuthHeaders(debugHeaders);
        persistSession({
          mode: 'debug',
          principal: livePrincipal,
          debugHeaders,
        });
        setAuthError('');
      },
      async signInWithCognito() {
        if (!authConfig?.cognitoDomain) {
          throw new Error('Cognito hosted sign-in is not configured.');
        }

        const loginRequest = await createHostedLoginRequest(authConfig);
        sessionStorage.setItem(PKCE_VERIFIER_KEY, loginRequest.verifier);
        sessionStorage.setItem(PKCE_STATE_KEY, loginRequest.state);
        window.location.assign(loginRequest.authorizeUrl);
      },
      signOut() {
        const stored = loadStoredSession();
        setPrincipal(null);
        setAuthHeaders({});
        clearStoredSession();
        clearPkceSession();

        if (stored?.mode === 'oauth' && authConfig?.cognitoDomain) {
          const logoutUrl = new URL(`${authConfig.cognitoDomain}/logout`);
          logoutUrl.searchParams.set('client_id', authConfig.cognitoClientId);
          logoutUrl.searchParams.set('logout_uri', authConfig.logoutRedirectUri);
          window.location.assign(logoutUrl.toString());
        }
      },
    }),
    [principal, authConfig, authBootstrapped, authError, authHeaders]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used inside SessionProvider.');
  }

  return context;
}

async function restoreOauthCallback(
  authConfig: AuthConfig,
  setSessionState: (principal: Principal, headers: Record<string, string>, session: StoredSession) => void
) {
  const callbackUrl = new URL(window.location.href);
  const code = callbackUrl.searchParams.get('code');
  const state = callbackUrl.searchParams.get('state');
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);

  if (!code || !state || !verifier || !expectedState || state !== expectedState) {
    throw new Error('Hosted sign-in callback validation failed.');
  }

  const tokens = await exchangeAuthorizationCode(authConfig, code, verifier);
  const headers = authorizationHeaders(tokens);
  const principal = await getCurrentPrincipal(headers);
  setSessionState(principal, headers, {
    mode: 'oauth',
    principal,
    tokens,
  });
  clearPkceSession();
  window.history.replaceState({}, document.title, callbackUrl.pathname);
}

function authorizationHeaders(tokens: CognitoTokens): Record<string, string> {
  return {
    Authorization: `${tokens.tokenType || 'Bearer'} ${tokens.idToken}`,
  };
}

function persistSession(session: StoredSession) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function loadStoredSession(): StoredSession | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

function clearStoredSession() {
  localStorage.removeItem(STORAGE_KEY);
}

function clearPkceSession() {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
}
